<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import AgentAccessLinksDialog from './AgentAccessLinksDialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';

const props = defineProps({
  agent: { type: Object, required: true },
  showToggleActive: { type: Boolean, default: true },
});

const emit = defineEmits(['toggleActive']);

const { t } = useI18n();
const store = useStore();
const currentUser = useMapGetter('getCurrentUser');
const isSuperAdmin = computed(() => currentUser.value.type === 'SuperAdmin');
// The backend rejects these same actions for a Super Admin's agent record unless the actor is
// also a Super Admin (see UserPolicy#manage_target?) - hide them here so the menu doesn't offer
// an action that will only come back as an error.
const canManageTarget = computed(
  () => isSuperAdmin.value || !props.agent.is_super_admin
);
// The backend also rejects direct-access/password-reset link generation for a peer
// administrator (see UserPolicy#peer_administrator?) - a regular admin should not be able to
// pull a live, unconsented session as another admin in the same account. A Super Admin actor is
// exempt, since their sanctioned support tool is the impersonation link below.
const isPeerAdministrator = computed(
  () => !isSuperAdmin.value && props.agent.role === 'administrator'
);
// The policy also refuses both links for a deactivated agent - neither would let them into this
// account anyway (see UserPolicy#target_deactivated?).
const canGenerateAccessLinks = computed(
  () =>
    canManageTarget.value &&
    !isPeerAdministrator.value &&
    !props.agent.deactivated
);

const showMenu = ref(false);
const isBusy = ref(false);
const accessLinksDialogRef = ref(null);

const menuItems = computed(() => {
  const items = [];
  if (props.showToggleActive && canManageTarget.value) {
    items.push({
      label: t(
        props.agent.deactivated
          ? 'AGENT_MGMT.ACTIVATE.BUTTON_TEXT'
          : 'AGENT_MGMT.DEACTIVATE.BUTTON_TEXT'
      ),
      action: 'toggle-active',
      value: 'toggle-active',
      icon: props.agent.deactivated ? 'i-lucide-user-check' : 'i-lucide-user-x',
    });
  }
  if (canGenerateAccessLinks.value) {
    items.push({
      label: t('AGENT_MGMT.ACCESS_LINKS.MENU_ITEM'),
      action: 'access_links',
      value: 'access_links',
      icon: 'i-lucide-link',
    });
  }
  if (isSuperAdmin.value) {
    items.push({
      label: t('AGENT_MGMT.ACCESS_LINKS.IMPERSONATE'),
      action: 'impersonate',
      value: 'impersonate',
      icon: 'i-lucide-user-cog',
    });
  }
  return items;
});

const openImpersonationLink = async () => {
  isBusy.value = true;
  try {
    const url = await store.dispatch(
      'agents/generateImpersonationLink',
      props.agent.id
    );
    window.open(url, '_blank', 'noopener,noreferrer');
  } catch (error) {
    useAlert(t('AGENT_MGMT.ACCESS_LINKS.API.ERROR_MESSAGE'));
  } finally {
    isBusy.value = false;
  }
};

const handleAction = ({ action }) => {
  showMenu.value = false;
  if (action === 'toggle-active') {
    emit('toggleActive');
  } else if (action === 'access_links') {
    accessLinksDialogRef.value.open();
  } else if (action === 'impersonate') {
    openImpersonationLink();
  }
};
</script>

<template>
  <div v-on-clickaway="() => (showMenu = false)" class="relative">
    <Button
      v-tooltip.top="$t('AGENT_MGMT.ACCESS_LINKS.MENU_TOOLTIP')"
      icon="i-lucide-ellipsis-vertical"
      slate
      sm
      :is-loading="isBusy"
      :class="showMenu ? 'bg-n-alpha-2' : ''"
      @click="showMenu = !showMenu"
    />
    <DropdownMenu
      v-if="showMenu"
      :menu-items="menuItems"
      class="ltr:right-0 rtl:left-0 mt-1 w-64 top-full"
      @action="handleAction($event)"
    />
    <AgentAccessLinksDialog ref="accessLinksDialogRef" :agent="agent" />
  </div>
</template>
